import React, { FC } from 'react';
import {
  Drawer,
  Grid,
  useTheme,
  drawerClasses,
  Backdrop,
  IconButton,
  lighten,
  Divider,
} from '@mui/material';
import Content from './DetailsContent';
import Header from './DetailsHeader';
import { connect } from 'react-redux';
import { RootState } from 'src/store';
import {
  detailsDrawerInit,
  getDetailsDrawerIsOpen,
} from '../store/detailsDrawerSlice';
import { Dispatch } from 'redux';
import { transition } from 'src/theme';
import { DETAILS_DRAWER_Z_INDEX } from './utils';
import XIcon from '@mui/icons-material/Close';
import Controller from './DetailsContent/DetailsController';

const CloseButton: FC<{ onClick: () => void; isOpen: boolean }> = ({
  isOpen,
  onClick,
}) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      right: '100%',
      padding: ({ spacing }) => spacing(0.5625),
      top: ({ spacing }) => spacing(1.5),
      zIndex: DETAILS_DRAWER_Z_INDEX - 1,
      background: ({ palette }) => palette.common.white,
      '&:hover': {
        background: ({ palette }) => palette.common.white,
        '& svg': {
          fill: ({ palette }) => lighten(palette.secondaryRed ?? '', 0.2),
        },
      },
      transition: transition({
        duration: isOpen ? 0.5 : 0.1,
        property: 'opacity',
        easing: 'ease-in-out',
      }),
      userSelect: 'none',
      opacity: isOpen ? 1 : 0,
      animationDelay: isOpen ? '0.5s' : '0.1s',
      transform: ({ spacing }) =>
        `translateX(${isOpen ? `-${spacing(1)}` : '200%'})`,
    }}
  >
    <XIcon
      sx={{
        fontSize: 26,
        fill: ({ palette }) => (isOpen ? palette.primary.light : 'transparent'),
        animationDelay: isOpen ? '0.75s' : 0,
        transition: transition({ easing: 'ease-in-out', duration: 0.5 }),
        '&:hover': {
          transition: transition({ easing: 'ease-in-out', duration: 0.3 }),
          animationDelay: 0,
        },
        opacity: isOpen ? 1 : 0,
        transform: `rotateZ(${isOpen ? 0 : 90}deg)`,
      }}
    />
  </IconButton>
);

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const DetailsDrawer: FC<Props> = ({ isOpen, onCloseDetailsDrawer }) => {
  const theme = useTheme();

  return (
    <>
      <Grid
        sx={{
          display: 'flex',
          // transition: theme.transitions.create('opacity', {
          //   easing: theme.transitions.easing.sharp,
          //   duration: theme.transitions.duration.enteringScreen,
          // }),
          overflow: 'hidden',
          width: 'fit-content',
        }}
      >
        <Drawer
          sx={{
            flexShrink: 0,
            position: 'relative',
            [`& .${drawerClasses.paper}`]: {
              maxWidth: theme.sizes.general.detailsDrawer.open,
              minWidth: theme.sizes.general.detailsDrawer.open,
              height: '100%',
              overflow: 'visible',
              background: theme.palette.common.white,
              transition: transition({ duration: 0.45 }),
              zIndex: DETAILS_DRAWER_Z_INDEX,
              borderTopLeftRadius: 24,
              borderBottomLeftRadius: 24,
              transform: `${isOpen ? 'translateX(0)' : 'translateX(100%)'} !important`,
              // boxShadow: 'rgba(0, 0, 0, 1) 20px 60px 50px 0px',
            },
          }}
          variant="persistent"
          anchor="right"
          open={isOpen}
        >
          <Header />
          <Divider />
          <Content />
          <CloseButton isOpen={isOpen} onClick={onCloseDetailsDrawer} />
        </Drawer>
      </Grid>
      <Backdrop
        open={isOpen}
        sx={{ zIndex: DETAILS_DRAWER_Z_INDEX - 1 }}
        onClick={onCloseDetailsDrawer}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOpen: getDetailsDrawerIsOpen(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onCloseDetailsDrawer: () => dispatch(detailsDrawerInit({ type: null })),
});

export default connect(mapStateToProps, mapDispatchToProps)(DetailsDrawer);
